import { useQueryExamAll } from "@/api/exam/exam";
import { RegistrationType } from "@/api/enums/RegistrationType";
import { ExamType } from "@/api/enums/ExamType";
import { useCandidateCertificates } from "@/hooks/useCandidateCertificates";
import { isWithinRenewalWindow } from "@/lib/renewal-window";
import { useMemo } from "react";

const examTypeByRegistrationType: Record<RegistrationType, ExamType[]> = {
  [RegistrationType.INITIAL]: [ExamType.INITIAL],
  [RegistrationType.EXTENSION]: [ExamType.INITIAL],
  [RegistrationType.RENEW]: [ExamType.RENEW],
};

type AvailableExamsError = "exams" | "certificates";

interface UseAvailableExamsProps {
  registrationType?: RegistrationType;
  candidateId?: number;
}

export const useAvailableExams = ({
  registrationType,
  candidateId,
}: UseAvailableExamsProps) => {
  const isRenewal = registrationType === RegistrationType.RENEW;

  const {
    data: exams,
    isPending: isPendingExams,
    isError: isErrorExams,
  } = useQueryExamAll();
  const {
    selectableCertificates,
    isPending: isPendingCerts,
    isError: isErrorCerts,
  } = useCandidateCertificates(candidateId);

  // undefined until there is a list to judge an exam against
  const registrableExams = useMemo(() => {
    if (!exams) return undefined;

    const now = new Date();

    const stillOpen = exams.filter((exam) => {
      if (new Date(exam.date) < now) return false;

      // an admin registering a candidate may pick an exam whose registrations
      // have already closed, up to the exam date itself
      return !!candidateId || new Date(exam.registeringEndDate) >= now;
    });

    if (!registrationType) return stillOpen;

    const allowedTypes = examTypeByRegistrationType[registrationType];
    return stillOpen.filter((exam) => allowedTypes.includes(exam.type));
  }, [exams, registrationType, candidateId]);

  const renewableExams = useMemo(() => {
    const registrable = registrableExams ?? [];

    if (registrationType !== RegistrationType.RENEW) return registrable;

    if (!selectableCertificates?.length) return [];

    return registrable.filter((exam) =>
      selectableCertificates.some((certificate) =>
        isWithinRenewalWindow(
          new Date(exam.date),
          new Date(certificate.expirationDate),
        ),
      ),
    );
  }, [registrableExams, selectableCertificates, registrationType]);

  const error: AvailableExamsError | undefined = isErrorExams
    ? "exams"
    : isRenewal && isErrorCerts
      ? "certificates"
      : undefined;

  return {
    exams: renewableExams,
    registrableExams,
    isLoading: isPendingExams || (isRenewal && isPendingCerts),
    error,
  };
};

export type { AvailableExamsError };
